import React, { useState, useMemo, Fragment } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import { Link } from 'react-router-dom';

import Table from 'components/Table';
import StatusBadge from 'components/StatusBadge';
import RefundActions from './RefundActions';

import { refundTable as TEXT } from 'locales/en/refund';
import { priceFromAPI } from 'utils/price';
import { CURRENCY_SYMBOL } from 'utils/transform';
import { DATE_WITH_HOUR, MONTH_DAY_HOUR } from 'utils/dateFormat';

const RefundTable = ({
  data,
  isDetail,
  topContent,
  pagination,
  onClickPerPage,
  onPageChange,
  onClickCta
}) => {
  const [activeItem, setActiveItem] = useState(null);
  const toggleActiveItem = (id) => {
    if (activeItem === id) {
      setActiveItem(null);
    } else {
      setActiveItem(id);
    }
  };

  const TITLES = useMemo(() => {
    let _titles = [
      TEXT.id,
      TEXT.status,
      TEXT.amount,
      TEXT.fee,
      TEXT.reason,
      ...(!isDetail ? [TEXT.orderId] : []),
      TEXT.date,
      ''
    ];
    if (!isDetail) {
      _titles.splice(5, 0, TEXT.transactionId);
    }
    return _titles;
  }, [isDetail]);

  const GRID_CLASSNAME = useMemo(() => {
    return isDetail ? 'grid-refunds' : 'grid-refunds-index';
  }, [isDetail]);

  const DATE_FORMAT = useMemo(() => {
    return isDetail ? DATE_WITH_HOUR : MONTH_DAY_HOUR;
  }, [isDetail]);

  return (
    <>
      {isDetail && (
        <h2 className="text-javelin-gray-dark lg:text-xl text-base font-bold mb-5">
          {TEXT.title}
        </h2>
      )}
      <Table
        titles={TITLES}
        hasData={!!data.length}
        gridClassName={GRID_CLASSNAME}
        topContent={topContent}
        pagination={pagination}
        onClickPerPage={onClickPerPage}
        onPageChange={onPageChange}
      >
        {data.map((item, idx) => {
          const dataIndexNumber = data.length - 1 === idx ? 'last' : idx;

          return (
            <Fragment key={item.id}>
              <Link
                data-index-number={dataIndexNumber}
                className="underline text-javelin-gray-dark text-xs py-3 px-5"
                to={`/transactions/${item.transaction_id}`}
                onClick={() => onClickCta(item.id)}
              >
                {item.id}
              </Link>
              <span
                data-index-number={dataIndexNumber}
                className="text-javelin-gray-dark text-xs py-3 pl-5"
              >
                <StatusBadge status={item.status} className="ml-0" />
              </span>
              <span
                data-index-number={dataIndexNumber}
                className="text-javelin-gray-dark text-xs py-3 px-5 font-bold"
              >
                {CURRENCY_SYMBOL[item.currency] || item.currency}
                {priceFromAPI(item.amount)}
              </span>
              <span
                data-index-number={dataIndexNumber}
                className="text-javelin-gray-dark text-xs py-3 px-5 font-bold"
              >
                {CURRENCY_SYMBOL[item.currency] || item.currency}
                {priceFromAPI(item.processing_fee)}
              </span>
              <span
                data-index-number={dataIndexNumber}
                className="text-javelin-gray-dark text-xs py-3 px-5"
              >
                {item.reason}
              </span>
              {!isDetail && (
                <Link
                  data-index-number={dataIndexNumber}
                  className="underline text-javelin-gray-dark text-xs py-3 px-5"
                  to={`/transactions/${item.transaction_id}`}
                >
                  {item.transaction_id}
                </Link>
              )}
              {(item.transaction) && (
                <span
                  data-index-number={dataIndexNumber}
                  className="underline text-javelin-gray-dark text-xs py-3 px-5"
                >
                  {item.transaction.data.order_id}
                </span>
              )}
              <span
                data-index-number={dataIndexNumber}
                className="text-javelin-gray-dark text-xs py-3 px-5"
              >
                {item.created_at
                  ? format(new Date(item.created_at), DATE_FORMAT)
                  : '-'}
              </span>
              {isDetail ? (
                <Link
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-light text-xs py-3 px-5 text-right cursor-pointer hover:text-javelin-gray-dark"
                  to={`/transactions/${item.transaction_id}`}
                  onClick={() => onClickCta(item.id)}
                >
                  <FontAwesomeIcon
                    icon={['far', 'eye']}
                    className="fill-current my-auto"
                  />
                </Link>
              ) : (
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs relative py-3 text-right pr-5"
                >
                  <RefundActions
                    onClickOpen={() => toggleActiveItem(item.id)}
                    isOpen={activeItem === item.id}
                    onClose={() => setActiveItem(null)}
                    item={item}
                    position={idx <= data.length - 3 ? 'bottom' : 'top'}
                  />
                </span>
              )}
            </Fragment>
          );
        })}
      </Table>
    </>
  );
};

RefundTable.propTypes = {
  data: PropTypes.array,
  isDetail: PropTypes.bool,
  topContent: PropTypes.node,
  pagination: PropTypes.shape({}),
  onClickPerPage: PropTypes.func,
  onPageChange: PropTypes.func,
  onClickCta: PropTypes.func
};
RefundTable.defaultProps = {
  data: [],
  onClickCta: () => ({})
};

export default RefundTable;
